import { ApiError, request } from "./api";
import type {
  Account,
  AccountInput,
  BillingPage,
  BillingStats,
  Invoice,
  InvoiceDetail,
  InvoiceInput,
  InvoiceListParams,
  InvoiceUpdateInput,
  PayInput,
  PayResult,
  Product,
  ProductInput,
  Quote,
  QuoteDetail,
  QuoteInput,
  QuoteListParams,
  QuoteUpdateInput,
  Tax,
  TaxInput,
  VoidInput,
} from "./billing-types";

type QueryValue = string | number | boolean | undefined;

function qs(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) {
      continue;
    }
    search.set(key, String(value));
  }
  const serialized = search.toString();
  return serialized === "" ? "" : `?${serialized}`;
}

export function fetchBillingStats(): Promise<BillingStats> {
  return request<BillingStats>("/billing/stats");
}

export function listQuotes(params: QuoteListParams = {}): Promise<BillingPage<Quote>> {
  return request<BillingPage<Quote>>(
    `/billing/quotes${qs({
      q: params.q,
      status: params.status,
      account_id: params.account_id,
      include_deleted: params.include_deleted ? "true" : undefined,
      cursor: params.cursor,
      limit: params.limit,
    })}`,
  );
}

export function getQuote(id: string): Promise<QuoteDetail> {
  return request<QuoteDetail>(`/billing/quotes/${encodeURIComponent(id)}`);
}

export function createQuote(input: QuoteInput): Promise<Quote> {
  return request<Quote>("/billing/quotes", { method: "POST", body: input });
}

export function updateQuote(id: string, input: QuoteUpdateInput): Promise<Quote> {
  return request<Quote>(`/billing/quotes/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: input,
  });
}

export function deleteQuote(id: string): Promise<{ id: string; deleted: boolean }> {
  return request<{ id: string; deleted: boolean }>(`/billing/quotes/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function sendQuote(id: string): Promise<Quote> {
  return request<Quote>(`/billing/quotes/${encodeURIComponent(id)}/send`, { method: "POST" });
}

export function acceptQuote(id: string): Promise<Quote> {
  return request<Quote>(`/billing/quotes/${encodeURIComponent(id)}/accept`, { method: "POST" });
}

export function rejectQuote(id: string): Promise<Quote> {
  return request<Quote>(`/billing/quotes/${encodeURIComponent(id)}/reject`, { method: "POST" });
}

export function listInvoices(params: InvoiceListParams = {}): Promise<BillingPage<Invoice>> {
  return request<BillingPage<Invoice>>(
    `/billing/invoices${qs({
      q: params.q,
      status: params.status,
      account_id: params.account_id,
      include_deleted: params.include_deleted ? "true" : undefined,
      cursor: params.cursor,
      limit: params.limit,
    })}`,
  );
}

export function getInvoice(id: string): Promise<InvoiceDetail> {
  return request<InvoiceDetail>(`/billing/invoices/${encodeURIComponent(id)}`);
}

export function createInvoice(input: InvoiceInput): Promise<Invoice> {
  return request<Invoice>("/billing/invoices", { method: "POST", body: input });
}

export function updateInvoice(id: string, input: InvoiceUpdateInput): Promise<Invoice> {
  return request<Invoice>(`/billing/invoices/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: input,
  });
}

export function deleteInvoice(id: string): Promise<{ id: string; deleted: boolean }> {
  return request<{ id: string; deleted: boolean }>(
    `/billing/invoices/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

export function sendInvoice(id: string): Promise<Invoice> {
  return request<Invoice>(`/billing/invoices/${encodeURIComponent(id)}/send`, { method: "POST" });
}

export function payInvoice(id: string, input: PayInput): Promise<PayResult> {
  return request<PayResult>(`/billing/invoices/${encodeURIComponent(id)}/pay`, {
    method: "POST",
    body: input,
  });
}

export function voidInvoice(id: string, input: VoidInput): Promise<Invoice> {
  return request<Invoice>(`/billing/invoices/${encodeURIComponent(id)}/void`, {
    method: "POST",
    body: input,
  });
}

export function listAccounts(): Promise<BillingPage<Account>> {
  return request<BillingPage<Account>>("/billing/accounts");
}

export function getAccount(id: string): Promise<Account> {
  return request<Account>(`/billing/accounts/${encodeURIComponent(id)}`);
}

export function createAccount(input: AccountInput): Promise<Account> {
  return request<Account>("/billing/accounts", { method: "POST", body: input });
}

export function updateAccount(id: string, input: Partial<AccountInput>): Promise<Account> {
  return request<Account>(`/billing/accounts/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: input,
  });
}

export function deleteAccount(id: string): Promise<{ id: string; deleted: boolean }> {
  return request<{ id: string; deleted: boolean }>(`/billing/accounts/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function listProducts(): Promise<BillingPage<Product>> {
  return request<BillingPage<Product>>("/billing/products");
}

export function getProduct(id: string): Promise<Product> {
  return request<Product>(`/billing/products/${encodeURIComponent(id)}`);
}

export function createProduct(input: ProductInput): Promise<Product> {
  return request<Product>("/billing/products", { method: "POST", body: input });
}

export function updateProduct(id: string, input: Partial<ProductInput>): Promise<Product> {
  return request<Product>(`/billing/products/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: input,
  });
}

export function deleteProduct(id: string): Promise<{ id: string; deleted: boolean }> {
  return request<{ id: string; deleted: boolean }>(`/billing/products/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function listTaxes(): Promise<BillingPage<Tax>> {
  return request<BillingPage<Tax>>("/billing/taxes");
}

export function getTax(id: string): Promise<Tax> {
  return request<Tax>(`/billing/taxes/${encodeURIComponent(id)}`);
}

export function createTax(input: TaxInput): Promise<Tax> {
  return request<Tax>("/billing/taxes", { method: "POST", body: input });
}

export function updateTax(id: string, input: Partial<TaxInput>): Promise<Tax> {
  return request<Tax>(`/billing/taxes/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: input,
  });
}

export function deleteTax(id: string): Promise<{ id: string; deleted: boolean }> {
  return request<{ id: string; deleted: boolean }>(`/billing/taxes/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function billingErrorCode(error: unknown): string | null {
  return error instanceof ApiError ? error.code : null;
}
